import { Injectable } from '@angular/core';
import { DataApiService } from './data-api-service';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  ScoreUtilsService,
  ScoreTimeSeriesContentParams,
} from '../../utils/score-utils.service';
import {
  ScoreTimeSeriesRequestBody,
  ScoreTimeSeriesResponse,
} from '../../models/score-time-series.interface';

/**
 * Parameters for getScoreTimeSeries method
 *
 * @example Standalone usage (chat-charts component)
 * {
 *   apiOverrides: {},
 *   params: {
 *     type: 'entities',
 *     score_type: 'esg',
 *     entity: ['bmw'],
 *     entityType: 'company',
 *     time: '2025-07-24--2025-10-24'
 *   }
 * }
 *
 * @example Query params usage (like news implementation)
 * {
 *   apiOverrides: {},
 *   params: {
 *     type: 'entities',
 *     score_type: 'esg'
 *     // entity and time resolved from URL query params
 *   }
 * }
 *
 * @example With API overrides
 * {
 *   apiOverrides: { collapse_on: ['participant'] },
 *   params: { type: 'entities', score_type: 'esg' }
 * }
 */
export interface ScoreTimeSeriesServiceParams {
  /**
   * Optional API fields to override in the final request
   * Use for fields like collapse_on or to override computed values
   */
  apiOverrides?: Partial<ScoreTimeSeriesRequestBody>;

  /**
   * Content params for building the request
   * - Explicit values (entity, time) → uses them
   * - No values → falls back to query params
   */
  params: ScoreTimeSeriesContentParams;
}

// Legacy alias for backward compatibility
export interface ScoreTimeSeriesParams {
  customParams: Partial<ScoreTimeSeriesRequestBody>;
  body: ScoreTimeSeriesContentParams;
}

@Injectable({
  providedIn: 'root',
})
export class ScoreTimeSeriesService {
  constructor(
    private dataApiService: DataApiService,
    private scoreRequestUtil: ScoreUtilsService,
  ) {}

  /**
   * Fetches score time series data from v2/api/score_ts
   */
  getScoreTimeSeries(
    params: ScoreTimeSeriesParams,
  ): Observable<ScoreTimeSeriesResponse> {
    const requestBody = this.scoreRequestUtil.createScoreTimeSeriesRequest(
      params.customParams,
      params.body,
    );
    return this.dataApiService
      .post<ScoreTimeSeriesResponse>('score_ts', requestBody, {}, 'v2')
      .pipe(switchMap((resp) => of(JSON.parse(JSON.stringify(resp)))));
  }
}
